import { useNavigate } from "react-router-dom";
import "./_style.scss";
import { useContext, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { headList, infoHeadList, monthMapping } from "./parts";
import TkTable from "../../components/atoms/TkTable";
import {
  httpIAPI_REC003,
  httpIAPI_REC004,
  httpIAPI_REC018,
} from "./api/request";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkDatePicker from "../../components/atoms/TkDatePicker";
import TkSelectForm from "../../components/atoms/TkSelectForm";
import { PaymentKbn, Status } from "../../assets/data/Constants";
import { TkGetCode, TkGetCodeList } from "../../utils/TkGetCode";
import TkButton from "../../components/atoms/TkButton";
import TkDataTable from "../../components/molecules/TkDataTable";
import TkBackButton from "../../components/molecules/TkBackButton";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import TkTitle from "../../components/atoms/TkTitle";
import { changeAmountFormat } from "../../utils/useTools";
import { toNumberOrUndefined } from "../../utils/useTools";

/**
 * SCRW160006 支払管理
 * @return SCRW160006 支払管理
 */
const SCRW160006 = () => {
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState, setState: setBusinessState } =
    useContext(BusinessContext);
  const context = businessState.paymentManagement?.searchForPayment;
  const nendoRef = useRef<number>();
  const [retirementPayList, setRetirementPayList] = useState<any>([]);
  const [bodyList, setBodyList] = useState<any>([]);
  const [infoBodyList, setInfoBodyList] = useState<any[]>([]);
  const [searchValues, setSearchValues] = useState<any>({});
  const [dateError, setDateError] = useState(false);
  const [selectItem, setSelectItem] = useState<any>([]);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [targetMonth, setTargetMonth] = useState<string>("");
  const [selectItemNew, setSelectItemNew] = useState<any>([]);
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(true);
  const possibilityFlgRef = useRef<string>("0");

  // 年度取得
  const handleSetNendo = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const fiscalYear = month >= 4 ? year : year - 1;
    return fiscalYear;
  };

  useEffect(() => {
    nendoRef.current = handleSetNendo();
    if (context) {
      setRetirementPayList(context.retirementPayList);
      setBodyList(setSimpleList(context.retirementPayList));

      let param = { ...context };
      delete param.retirementPayList;
      handleSearch(param);
    } else {
      handleInit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInit = async () => {
    try {
      const res = await httpIAPI_REC003({ nendo: nendoRef?.current });

      if (res.resultCd === "I0000") {
        possibilityFlgRef.current = res?.possibilityFlg!;
        setRetirementPayList(res.retirementPayList);
        setBodyList(setSimpleList(res.retirementPayList));
        setInfoList(res.managementInformationList);
        setIsDownloadDisabled(!(res.possibilityFlg === "1"));
        setTargetMonth("all");

        setBusinessState({
          ...businessState,
          paymentManagement: {
            searchForPayment: {
              retirementPayList: res.retirementPayList,
            },
          },
        });
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const saveTargetMonth = (value: string) => {
    setTargetMonth(value);
  };

  const searchByMonth = async () => {
    if (targetMonth !== "all") {
      setBodyList(
        setSimpleList(
          retirementPayList.filter(
            (item: any) => targetMonth === item.aggregationMonth
          )
        )
      );
    } else {
      setBodyList(setSimpleList(retirementPayList));
    }
  };

  useEffect(() => {
    searchByMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMonth]);

  const setSimpleList = (data: any) => {
    const result = data?.find((list: any) => {
      if (targetMonth === "all") {
        return list?.aggregationMonth === "0";
      } else {
        return list?.aggregationMonth === targetMonth.replace("月", "");
      }
    });

    return [
      [
        {
          children: (
            <TkSelectForm
              onGetValue={saveTargetMonth}
              selectPropsList={monthMapping}
              wantToSetValue={targetMonth}
            />
          ),
          aggregationMonth: result?.aggregationMonth,
        },
        {
          children: (result?.billerCount ?? 0) + "件",
          align: "center",
        },
        {
          children:
            (toNumberOrUndefined(result?.billerAmount) ?? 0).toLocaleString() +
            "円",
          align: "center",
        },
        {
          children: (result?.averageProcessingDays ?? 0) + "日",
          align: "center",
        },
      ],
    ];
  };

  const setInfoList = (data: any) => {
    let infoBodyList = data?.map((item: any) => [
      {
        children: item.invoiceClass,
        disabledCheckBox:
          possibilityFlgRef.current === "0" ||
          setCheckBoxStatus(item.downloadStatus, item.billerStatus),
        transferId: item.transferId,
        transferDataName: item.transferDataName,
        forSort: item.invoiceClass,
      },
      {
        children: item.dataCreateDate,
        align: "center",
        forSort: item.dataCreateDate,
      },
      {
        children: item.transferRequestDate,
        align: "center",
        forSort: item.transferRequestDate,
      },
      {
        children: (
          <Box>
            {item.downloadStatus === "1" ? (
              <Box>
                <Typography>ダウンロード済み</Typography>
                <Typography>({item.downloadDate})</Typography>
              </Box>
            ) : (
              "-"
            )}
          </Box>
        ),
        align: "center",
        forSort: item.downloadStatus,
      },
      {
        children:
          item.invoiceClass === "退職手当金" ||
          item.invoiceClass === "退職手当金再送金" ? (
            <Box>
              <Typography>
                {"新規決定分:" + (item.newCount ?? 0) + "件"}
              </Typography>
              <Typography>
                {"再送金分:" + (item.remittanceCount ?? 0) + "件"}
              </Typography>
              <Typography>
                {"追加支給分:" + (item.additionalPaymentCount ?? 0) + "件"}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography>{(item.otherCount ?? 0) + "件"}</Typography>
            </Box>
          ),
        align: "left",
      },
      {
        children:
          item.invoiceClass === "退職手当金" ||
          item.invoiceClass === "退職手当金再送金" ? (
            <Box>
              <Typography>
                {"新規決定分:" +
                  changeAmountFormat(item.newPayment ?? 0) +
                  "円"}
              </Typography>
              <Typography>
                {"再送金分:" +
                  changeAmountFormat(item.remittancePayment ?? 0) +
                  "円"}
              </Typography>
              <Typography>
                {"追加支給分:" +
                  changeAmountFormat(item.additionalPayment ?? 0) +
                  "円"}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography>
                {changeAmountFormat(item.otherPayment ?? 0) + "円"}
              </Typography>
            </Box>
          ),
        align: "right",
      },
      {
        children: item.transferDataName,
        align: "left",
        forSort: item.transferDataName,
      },
      {
        children: TkGetCode(Status, item.billerStatus),
        align: "left",
        forSort: item.billerStatus,
      },
    ]);

    setInfoBodyList([...infoBodyList]);
  };

  const handleChangeSearch = (field: string, value: any) => {
    let newValue = {
      ...searchValues,
      [field]: value,
    };
    !value && delete newValue[field];
    setSearchValues(newValue);
  };

  // 振込データ参照API
  const handleSearch = async (defaultSearch?: any) => {
    if (dateError) return;

    let param = defaultSearch ?? searchValues;
    try {
      const res = await httpIAPI_REC004({
        ...param,
        nendo: nendoRef?.current,
      });

      if (res.resultCd === "I0000") {
        setInfoList(res.transferInformationList);
        setBusinessState({
          paymentManagement: {
            searchForPayment: { ...context, ...param },
          },
        });
        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setInfoList([]);
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const handleJump = () => {
    navigate("/SCRW160007");
  };

  const dataURLtoFile = (dataStr: string) => {
    const mimeType = "application/octet-stream,charset=UTF-8";
    let cleanedData = dataStr.split(",")[1];
    const bstr = atob(cleanedData);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mimeType });
  };

  const downloadFile = (fileName: string, dataStr: string) => {
    const blob = dataURLtoFile(dataStr);
    const eleLink = document.createElement("a");
    eleLink.download = fileName;
    eleLink.style.display = "none";
    eleLink.href = URL.createObjectURL(blob);
    document.body.appendChild(eleLink);
    eleLink.click();
    document.body.removeChild(eleLink);
  };

  const handleOK = async () => {
    try {
      const reqDownloadList = selectItem.map((item: any) => {
        return {
          transferId: item,
        };
      });
      const res = await httpIAPI_REC018({
        downloadList: reqDownloadList,
      });
      if (res.resultCd === "I0000") {
        const seletedItems = infoBodyList.filter((item: any, index: number) =>
          selectItemNew.includes(index)
        );
        const selectedItemsTransferIds = seletedItems.map((item: any) => {
          return item[0].transferId;
        });
        res.paymentManageList &&
          res.paymentManageList.forEach((element) => {
            if (selectedItemsTransferIds.indexOf(element.transferId) !== -1) {
              const fileName = seletedItems.find(
                (item: any) => item[0].transferId === element.transferId
              );
              downloadFile(fileName[0].transferDataName, element.file!);
            }
          });
        setOpenSuccess(false);
        handleSearch();
      } else {
        setState({
          ...state,
          errorMsgId: "",
        });
      }
    } catch (error) {
      navigate("/error");
    }
  };

  const setCheckBoxStatus = (downloadStatus: string, billerStatus: string) => {
    if (
      downloadStatus === "1" ||
      !(billerStatus === "08" || billerStatus === "11" || billerStatus === "13")
    ) {
      return true;
    } else {
      return false;
    }
  };

  const onCheckSectionWidth = (e: any) => {
    setSelectItemNew(e);
    const selectedItems = infoBodyList.filter((item: any, index: number) =>
      e.includes(index)
    );
    const selectedItemsTransferIds = selectedItems.map((item: any) => {
      return item[0].transferId;
    });
    setSelectItem(selectedItemsTransferIds);
  };

  const dateValid = {
    minDate: new Date("1900/01/01"),
    maxDate: new Date("2099/12/31"),
  };

  return (
    <Box className="SCRW160006" data-testid="SCRW160006PageForTest">
      <TkTitle size="medium">
        {nendoRef?.current}年度退職手当金の支給実績
      </TkTitle>
      <TkTable headList={headList} bodyList={bodyList} />

      <Box className="searchForm">
        <TkSelectForm
          placeholder="区分"
          selectPropsList={TkGetCodeList(PaymentKbn)}
          onGetValue={(e: any) => handleChangeSearch("searchKbn", e)}
          wantToSetValue={context?.searchKbn}
        />
        <Typography>振込予定日:</Typography>
        <TkDatePicker
          onChange={(e: any) =>
            handleChangeSearch("searchTransferScheduledDate", e.target.value)
          }
          onGetErrorFlg={setDateError}
          wantToSetValue={context?.searchTransferScheduledDate}
          {...dateValid}
        />
        <TkButton onClick={handleSearch}>検索</TkButton>
      </Box>

      <Box className="spanRight">
        <TkButton onClick={handleJump}>新規振込データ作成</TkButton>
      </Box>
      <TkDataTable
        headList={infoHeadList}
        bodyList={infoBodyList}
        count={infoBodyList.length}
        pagin
        checkSection
        defaultRowsPerPage={10}
        onCheckSectionWidth={(indexArr: any) => onCheckSectionWidth(indexArr)}
        showUnit={true}
      />

      <Box className="btnGroup">
        <TkBackButton />
        <TkButton
          data-testid="registerSuccess"
          onClick={() => setOpenSuccess(true)}
          disabled={isDownloadDisabled || !selectItem.length}
        >
          ダウンロード
        </TkButton>
      </Box>

      <TkMessageDialog
        data-testid="dialogTest"
        open={openSuccess}
        onOky={handleOK}
        onCancel={() => setOpenSuccess(false)}
        title={"ダウンロード"}
        contextText="　振込データダウンロードを行いますか？"
        cancelText="いいえ"
        okText="はい"
      />
    </Box>
  );
};

export default SCRW160006;
